import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import Icon from "../components/Icon";

export default function TajweedPage() {
  useScrollReveal();
  return (
    <>
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-20 md:pb-32 bg-surface-bright">
        <div className="absolute inset-0 geometric-pattern-emerald opacity-40"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-tertiary-fixed opacity-20 blur-3xl rounded-full"></div>
        <div className="relative page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary-container text-on-primary-container font-label-md text-label-md">
              <Icon name="stars" className="material-symbols-outlined text-sm" />
              Advanced Accuracy
            </div>
            <h1 className="text-display-responsive text-primary leading-tight">
              Tajweed Mastery Program
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              Learn the science of beautiful and accurate Quranic recitation. Master the precise rules of articulation, pronunciation, and rhythm with expert 1-on-1 instruction.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 pt-4">
              <Link
                to="/contact"
                className="inline-block bg-primary text-on-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center font-headline-md text-headline-md shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 md:border-8 border-white">
              <img
                alt="Student learning Tajweed rules"
                className="w-full h-auto object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuBKI0-6lrWTzTqEDUtWaLGRqtYXcOyz7AlVXgVLAV47p8XtGQ3DmJjIvhNUKWO5VMOPkNRTdgl2a7S5oeI2l7SrmGRzUpSa81h-ILZjf4tgy4ChvNRYkxbZ9OnB58pQPnNv8PoqPu0EHVbe0gNu_iqGslhPOc1rJAwMOpXhMRYWPUrR5TM00sIe5jo-83CQhyECVzccWYzJ9NcVErtHNGA2lj1YrxC82ADkMYRM_fYm2yAK0Fn1PTBGsEBwrQrNQTfbz6xpfGS_xwDD"
              />
            </div>
            <div className="relative md:absolute md:-bottom-8 md:-left-8 mt-6 md:mt-0 bg-white p-4 sm:p-6 rounded-2xl shadow-xl border border-outline-variant flex items-center gap-4 max-w-[calc(100vw-2rem)] md:max-w-none">
              <div className="bg-tertiary-container p-3 rounded-full">
                <Icon
                  name="record_voice_over"
                  className="material-symbols-outlined text-on-tertiary-container"
                  filled
                />
              </div>
              <div>
                <p className="font-label-md text-label-md text-on-surface-variant">
                  Rule Excellence
                </p>
                <p className="font-headline-md text-headline-md text-primary">
                  Tajweed Mastery
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="page-x max-w-max-width mx-auto">
          <div className="text-center mb-10 md:mb-16 space-y-4">
            <h2 className="text-headline-responsive text-primary">
              Expected Outcomes
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-2xl mx-auto">
              Our systematic approach breaks down complex pronunciation and recitation rules into simple, practical guidelines.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-gutter">
            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="spatial_audio"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Perfect Makharij
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Master the origin points in the throat and mouth to pronounce every Arabic letter with absolute precision.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="grid_goldenratio"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Madd &amp; Stops
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Understand rules of elongation (Madd), pausing, and stopping to maintain the rhythm and meaning of each verse.
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="campaign"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Vocal Rules
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Learn rules of Nun and Meem Sakinah, Qalqalah (vocal bouncing), and Tafkheem (heavy pronunciation).
              </p>
            </div>

            <div className="group card-pad rounded-2xl border border-outline-variant hover:border-primary hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="w-16 h-16 bg-surface-container rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-container transition-colors">
                <Icon
                  name="music_note"
                  className="material-symbols-outlined text-primary text-3xl group-hover:text-on-primary-container"
                />
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface mb-3">
                Beautiful Melody
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Recite the Quran with a beautiful, steady, and melodious tone that inspires and touches the heart.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-surface-container-low">
        <div className="page-x max-w-max-width mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 space-y-8">
            <h2 className="text-headline-responsive text-primary">
              Who is this program for?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-tertiary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-tertiary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Fluent Readers
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Students who can read the Quran but want to refine their pronunciation and apply professional recitation rules.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center">
                  <Icon
                    name="done"
                    className="material-symbols-outlined text-on-primary-fixed"
                  />
                </div>
                <div>
                  <h4 className="font-headline-md text-headline-md text-on-surface">
                    Hifz Preparation
                  </h4>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Memorizers seeking to secure perfect recitation before or during their memorization journey.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-white p-8 rounded-3xl border border-outline-variant shadow-lg space-y-6">
            <h3 className="font-headline-md text-primary text-center">Mastery Areas</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Makharij al-Huroof</span>
                  <p className="font-body-md text-on-surface-variant">Focusing on standard Arabic articulation points for each letter.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Sifaat al-Huroof</span>
                  <p className="font-body-md text-on-surface-variant">Learning the unique characteristics and voice modifications of letters.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" className="text-primary mt-1" />
                <div>
                  <span className="font-bold text-on-surface">Practical Application</span>
                  <p className="font-body-md text-on-surface-variant">Live recitation feedback during sessions to correct mistakes in real-time.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section-y page-x max-w-max-width mx-auto">
        <div className="gradient-mesh rounded-section p-8 md:p-16 flex flex-col md:flex-row items-center justify-between border border-primary-fixed/30 gap-8">
          <div className="max-w-xl">
            <h2 className="text-display-responsive text-primary mb-4 font-bold">
              Ready to beautify your Quran recitation?
            </h2>
            <p className="text-body-lg text-on-surface-variant">
              Begin your Tajweed journey with our certified Islamic scholars and learn to recite the Quran just as it was revealed.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <Link
              to="/contact"
              className="bg-primary text-on-primary px-8 py-4 rounded-full text-center font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              Book Free Trial
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
